import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Collapsible from 'react-collapsible';
import { TooltipItem } from 'shared/components/index';
import { TIMING_CATEGORIES, ROOM_STATUS } from 'shared/constants';
import WarningSmallIcon from 'assets/svgs/warningSmallIcon.svg';
import CheckedCircle from 'assets/svgs/checkedCircleBold.svg';
import NotApplicable from 'assets/svgs/notApplicable.svg';
import BuildingIcon from 'assets/svgs/buildingIcon.svg';
import './RoomReviewDetails.scss';

const RoomsReviewDetails = (props) => {
  const { t } = useTranslation();
  return (
    <div className="room-review-section">
      <div className="h4">{t('tcEventDashboard.checkout.rooms.title')}</div>
      {props.roomsList.map((listByLocation, idx) => (
        <Collapsible
          open
          key={`list-${idx}`}
          trigger={
            <div className="h5 mb-0 trigger-content">
              <BuildingIcon />
              {listByLocation[0].locationName}
              {listByLocation[0].locationArea ? `, ${listByLocation[0].locationArea}` : ''}
            </div>
          }>
          <div className="rooms-grid">
            <div className="fw-bold table-header">
              <div className="hide-mobile">
                {t('tcEventDashboard.checkout.rooms.tableHeaders.name')}
              </div>
              <div className="hide-mobile"></div>
              <div className="">{t('tcEventDashboard.checkout.rooms.tableHeaders.closed')}</div>
              <div className="">
                {t('tcEventDashboard.checkout.rooms.tableHeaders.seatingChart')}
              </div>
              <div className="">{t('tcEventDashboard.checkout.rooms.tableHeaders.timing')}</div>
            </div>
            {listByLocation.map((room, i) => (
              <div
                className={`${room.roomStatus === ROOM_STATUS.inactive ? 'inactive-row' : ''}`}
                key={`room-${i}`}>
                <TooltipItem item={room.roomName} customClass="room-name" />
                {room.roomStatus === ROOM_STATUS.inactive ? (
                  <div className="entire-row">
                    {t('tcEventDashboard.checkout.rooms.statusUnknown')}
                  </div>
                ) : (
                  <>
                    <div className="type-timing-labels">
                      <span className="label-box-radius">
                        {room.roomTypeName
                          ? room.roomTypeName
                          : t('testCenter.roomDashboard.noTypeLabel')}
                      </span>
                      {room.timing && (
                        <span className="label-box-radius">
                          {props.mappedRoomTiming[room.timing]?.name}
                        </span>
                      )}
                      <span className="label-box-radius">
                        {room.onlineFlag ? t('common.labels.online') : t('common.labels.paper')}
                      </span>
                    </div>
                    <div className="icon-container">
                      {room.roomStatus === ROOM_STATUS.closed ? (
                        <CheckedCircle />
                      ) : (
                        <TooltipContainer
                          content={t('tcEventDashboard.checkout.rooms.closedWarningTooltip')}
                          icon={WarningSmallIcon}
                        />
                      )}
                    </div>
                    <div className="icon-container">
                      {room.testedStatusUnknown === 0 ? (
                        <CheckedCircle />
                      ) : (
                        <TooltipContainer
                          content={t('tcEventDashboard.checkout.rooms.seatingChartWarningTooltip')}
                          icon={WarningSmallIcon}
                        />
                      )}
                    </div>
                    <div className="icon-container">
                      {room.timersCount === 0 ? (
                        <TooltipContainer
                          content={t('tcEventDashboard.checkout.rooms.notApplicableTooltip')}
                          icon={NotApplicable}
                        />
                      ) : room.timersMissingCount === 0 ? (
                        <CheckedCircle />
                      ) : (
                        <TooltipContainer
                          content={t('tcEventDashboard.checkout.rooms.timingWarningTooltip')}
                          icon={WarningSmallIcon}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </Collapsible>
      ))}
    </div>
  );
};

export default RoomsReviewDetails;

RoomsReviewDetails.propTypes = {
  roomsList: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        locationName: PropTypes.string,
        locationArea: PropTypes.string,
        roomName: PropTypes.string,
        roomTypeName: PropTypes.string,
        roomStatus: PropTypes.string,
        onlineFlag: PropTypes.bool,
        timing: PropTypes.string,
        timersCount: PropTypes.number,
        timersMissingCount: PropTypes.number,
        testedStatusUnknown: PropTypes.number,
      })
    )
  ).isRequired,
  mappedRoomTiming: PropTypes.objectOf(
    PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.oneOf(Object.values(TIMING_CATEGORIES)),
    })
  ).isRequired,
};

const TooltipContainer = (props) => {
  const Icon = props.icon;
  return (
    <div
      data-bs-toggle="popover"
      data-bs-placement="top"
      data-bs-trigger="hover"
      data-bs-boundary="window"
      data-bs-content={props.content}>
      <Icon />
    </div>
  );
};
